import { RewardProviderType } from '@kudos/http-client'
import { StaticPaths, TFunction } from '@shared/types/types'
import AppPaths from '.'

/**
 * Function that returns page titles from static routes.
 * @param t - translation TFunction
 * @returns an object with paths and translated titles as key value pairs.
 */
export const getPathTitlesStatic = (t: TFunction): StaticPaths => ({
  //AdminPaths
  [AppPaths.tenant.admin.members('users')]: t('Users'),
  [AppPaths.tenant.admin.members('teams')]: t('Teams'),
  [AppPaths.tenant.admin.members('quick-teams')]: t('Quick teams'),
  [AppPaths.tenant.admin.customize()]: t('Branding'),
  [AppPaths.tenant.admin.localization()]: t('Language & time'),
  [AppPaths.tenant.admin.hrisIntegrations('records')]: t('HRIS integration'),
  [AppPaths.tenant.admin.security()]: t('Security & access'),
  [AppPaths.tenant.admin.checkInManage()]: t('Configure check-ins'),
  [AppPaths.tenant.admin.analytics('insights')]: t('Burnout insights'),
  [AppPaths.tenant.admin.analytics('dashboards')]: t('Check-ins'),
  [AppPaths.tenant.admin.createTeam()]: t('Team configuration'),
  [AppPaths.tenant.admin.configureQuickTeams()]: t('Configure quick teams'),
  [AppPaths.tenant.admin.values()]: t('Values & qualities'),
  [AppPaths.tenant.admin.configureValues()]: t('Add value'),
  [AppPaths.tenant.admin.kudosTvIntegrations()]: t('Kudos TV'),
  [AppPaths.tenant.admin.points('preferences')]: t('Points preferences'),
  [AppPaths.tenant.admin.points('allocation')]: t('Points allocation'),
  [AppPaths.tenant.admin.notifications()]: t('Notifications'),
  [AppPaths.tenant.admin.rewards('setup')]: t('Reward stores'),
  [AppPaths.tenant.admin.configureRewardProvider(RewardProviderType.TANGO)]: t('Add reward store'),
  [AppPaths.tenant.admin.configureRewardProvider(RewardProviderType.CUSTOM)]:
    t('Add custom reward store'),
  [AppPaths.tenant.admin.logs()]: t('Logs'),
  [AppPaths.tenant.admin.pointsRules('bank')]: t('Point banks'),
  [AppPaths.tenant.admin.pointsRules('celebration')]: t('Celebration points'),
  [AppPaths.tenant.admin.pointsRules('peer')]: t('Peer-to-peer points'),
  [AppPaths.tenant.admin.configureCelebrations('privacy')]: t('Celebrations'),
  [AppPaths.tenant.admin.configureCelebrations('permission')]: t('Celebrations'),
  [AppPaths.tenant.admin.configureCelebrations('cards')]: t('Celebrations'),
  [AppPaths.tenant.admin.configureAwardType()]: t('Create award'),
  [AppPaths.tenant.admin.awards('awards')]: t('Awards'),
  [AppPaths.tenant.admin.awards('nominations')]: t('Nominations'),
  [AppPaths.tenant.admin.manageIncentives()]: t('Incentives'),
  [AppPaths.tenant.admin.configureIncentive()]: t('Create incentive'),
  [AppPaths.tenant.admin.allReports()]: t('Reports'),
  [AppPaths.tenant.admin.announcements()]: t('Configure announcements'),
  [AppPaths.tenant.admin.configureAnnouncement()]: t('Create announcement'),
  [AppPaths.tenant.admin.flaggedContent('awards')]: t('Flagged awards'),
  [AppPaths.tenant.admin.flaggedContent('cards')]: t('Flagged cards'),
  [AppPaths.tenant.admin.flaggedContent('comments')]: t('Flagged comments'),
  [AppPaths.tenant.admin.flaggedContent('recognition')]: t('Flagged recognitions'),
  [AppPaths.tenant.admin.dashboard()]: t('Dashboard'),
  [AppPaths.tenant.admin.integrations()]: t('Integrations'),
  [AppPaths.tenant.admin.ftpIntegrations('connections')]: t('FTP connections'),
  [AppPaths.tenant.admin.ftpIntegrations('history')]: t('FTP sync history'),
  [AppPaths.tenant.admin.configureFtp()]: t('Create FTP connection'),
  [AppPaths.tenant.admin.ms365Integrations()]: t('Microsoft 365'),
  [AppPaths.tenant.admin.ssoIntegrations()]: t('SSO'),
  [AppPaths.tenant.admin.manageRecognition()]: t('Recognition'),
  [AppPaths.tenant.admin.delegation()]: t('Delegation'),
  [AppPaths.tenant.admin.delegateAwards()]: t('Awards'),
  [AppPaths.tenant.admin.spacesManage('overview')]: t('Spaces overview'),
  [AppPaths.tenant.admin.spacesManage('permission')]: t('Spaces permission'),

  // AnonPaths
  [AppPaths.anon.userLogin()]: t('User login'),
  [AppPaths.anon.userLoginChallenge()]: t('Login challenge'),
  [AppPaths.anon.passwordRecovery()]: t('Recover password'),
  [AppPaths.anon.tenantLogin()]: t('Tenant login'),
  [AppPaths.anon.resetPassword()]: t('Reset password'),
  [AppPaths.anon.maintenance()]: t('Maintenance'),
  [AppPaths.anon.linkExpired()]: t('Link expired'),
  [AppPaths.anon.passwordExpired()]: t('Password expired'),
  [AppPaths.anon.passwordSetup()]: t('Password setup'),

  // TenantScopedPaths
  [AppPaths.tenant.home()]: t('Home'),
  [AppPaths.tenant.sendRecognition()]: t('Send recognition'),
  [AppPaths.tenant.viewRecognition(':id')]: t('Recognition'),
  [AppPaths.tenant.viewDraftRecognition(':id')]: t('Edit recognition'),
  [AppPaths.tenant.me()]: t('My profile'),
  [AppPaths.tenant.mySettings('notifications')]: t('Notification settings'),
  [AppPaths.tenant.mySettings('general')]: t('General settings'),
  [AppPaths.tenant.mySettings('privacy')]: t('Privacy settings'),
  [AppPaths.tenant.mySettings('security')]: t('Security settings'),
  [AppPaths.tenant.mySettings('integrations')]: t('Integrations'),
  [AppPaths.tenant.changePassword()]: t('Change password'),
  [AppPaths.tenant.resourceHub('all')]: t('All resources'),
  [AppPaths.tenant.resourceHub('recommended')]: t('Recommended resources'),
  [AppPaths.tenant.resourceHub('favourites')]: t('Favourite resources'),
  [AppPaths.tenant.rewardsProviders()]: t('Rewards providers'),
  [AppPaths.tenant.yourDirectReports()]: t('Your direct reports'),
  [AppPaths.tenant.awards()]: t('Send award'),
  [AppPaths.tenant.celebrate('calendar')]: t('Calendar'),
  [AppPaths.tenant.celebrate('cards')]: t('Cards'),
  [AppPaths.tenant.configureCard()]: t('Create card'),
  [AppPaths.tenant.redemptionHistory()]: t('Redemption history'),
  [AppPaths.tenant.incentives()]: t('Incentives'),
  [AppPaths.tenant.culture()]: t('Culture'),
  [AppPaths.tenant.people()]: t('User directory'),
  [AppPaths.tenant.spaces()]: t('Spaces'),
  [AppPaths.tenant.configureSpace()]: t('Create space'),

  // Mobile only paths
  [AppPaths.tenant.notificationActivity()]: t('Activity'),
  [AppPaths.tenant.mySettingsMenu()]: t('My settings'),

  // UserScopedPaths
  [AppPaths.user.tenants()]: t('My tenants'),
})
